import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subject } from 'rxjs';

import {
	ComponentBase,
	CompanyService,
	Breadcrumb,
	BreadcrumbsService,
	DeploymentContext,
	SearchService,
	Summary,
	WebAnalyticsService,
} from '@Common';

import {
	AggregateAssetResult,
	Filter,
	Mode,
	TagWithCount,
} from 'company-finder-common';
@Component({
	selector: 'main',
	templateUrl: './main.component.html',
	styleUrls: ['./main.component.scss'],
	standalone: false,
})
export class MainComponent extends ComponentBase implements OnDestroy, OnInit {
	public comprehensiveSummary: Summary;
	public activeSummary: Summary;
	public tagCounts: TagWithCount[];
	private _breadcrumb: Breadcrumb;

	public constructor(
		dc: DeploymentContext,
		private breadcrumbsService: BreadcrumbsService,
		private companyService: CompanyService,
		private router: Router,
		private searchService: SearchService,
		private webAnalyticsService: WebAnalyticsService
	) {
		super(dc);

		// This was lifted up from the company overview component when that
		// became conditional with the addition of asset mode
		// FUTURE - consider a resolver or guard
		router.events.subscribe((event: NavigationStart) => {
			// Only clear the filter when returning to the main/overview page
			if (event.navigationTrigger === 'popstate' && event.url === '/') {
				searchService.clearAllFilters(true);
			}
		});
	}

	private get filter(): Filter {
		return this.searchService.filter;
	}

	private get filterSubject(): Subject<Mode> {
		return this.searchService.filterSubject;
	}

	public assetSummary: AggregateAssetResult;

	private async loadSearch(): Promise<void> {
		switch (this._deploymentContext.currentMode) {
			case Mode.Asset: {
				this.assetSummary =
					await this.searchService.searchAssetAggregate();
				return;
			}
			case Mode.Company:
			default: {
				const result =
					await this.searchService.searchCompanyAggregate();
				this.activeSummary = new Summary(result);
			}
		}
	}

	public ngOnInit(): void {
		this.initializeData();
		this.addSubscription(
			this.filterSubject.subscribe(async () => {
				// NOTE: This does not trigger navigation to search results.
				this.loadSearch();
			})
		);

		// FUTURE: This action implementation completely replaces, but with very similar content,
		//  what the BreadcrumbsService constructor itself established.  Would be nice to avoid duplicate logic if possible.
		//  This implementation is used the breadcrumbs are used on a MainComponent view (e.g Landing Page or Search Results).
		this.breadcrumbsService.rootBreadcrumb.extraAction = async () => {
			await this.initializeData();
		};
	}

	public ngOnDestroy(): void {
		if (this._subscriptions) {
			this._subscriptions.unsubscribe();
			this._subscriptions = undefined;
		}
	}

	public clearSearchResults(): void {}

	private async initializeData(): Promise<void> {
		// Per Brittany: Never want search terms to be preserved when entering the Overview screen
		//  either via Breadcrumb (cleared separately) or via browser back button (cleared by the logic below)
		this.searchService.clearSearchPredicate();

		this.assetSummary =
			await this.searchService.searchAssetAggregateForFilter(
				this.searchService.assetFilter
			);

		this.tagCounts = await this.searchService.getTagCounts();
		this.activeSummary = await this.companyService.filterSummary(
			this.searchService.companyFilter
		);
	}
}
